import {
  EEventContextAction,
  EEventType,
  type IEshopReview,
  type IEshopReviewDescription,
} from '@mahawi/eshop-common/dist/src/types';
import { type Dispatch } from '@reduxjs/toolkit';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';

import { Add } from '../components/icons/add';
import { Remove } from '../components/icons/remove';
import LinearProgress from '../components/linear-progress';
import AggregateStars from '../components/product/stars';
import ReviewAuthenticityNotice from '../components/review/review-authenticity-notice';
import Stars from '../components/review/stars';
import { eventSend } from '../reducers/event/reducer';
import { type ILanguageState } from '../reducers/language/types';
import { setActiveCategory } from '../reducers/products/reducer';
import { loadAllReviews } from '../reducers/reviews/reducer';
import { type IReviewsState } from '../reducers/reviews/types';

function ReviewsContainer({
  dispatch,
  Reviews,
  Language,
}: {
  dispatch: Dispatch;
  Reviews: IReviewsState;
  Language: ILanguageState;
}): React.JSX.Element {
  const { t } = useTranslation();

  useEffect(() => {
    dispatch(
      eventSend({
        type: EEventType.PAGE_REVIEWS,
        action: EEventContextAction.ENTER,
      }),
    );
    dispatch(
      setActiveCategory({
        category: {
          uuid: '',
        },
      }),
    );

    return (): void => {
      dispatch(
        eventSend({
          type: EEventType.PAGE_REVIEWS,
          action: EEventContextAction.LEAVE,
        }),
      );
    };
  }, [dispatch]);

  useEffect((): void => {
    if (!Reviews.reviews) {
      dispatch(loadAllReviews());
    }
  }, [dispatch, Reviews.reviews]);

  if (!Reviews.reviews) {
    return <LinearProgress />;
  }

  const reviewsWithRating = Reviews.reviews.filter(
    (r: IEshopReview): boolean => r.rating != null,
  );

  const averageRating: number | null =
    reviewsWithRating.length > 0
      ? Math.round(
          (reviewsWithRating.reduce(
            (sum: number, r: IEshopReview): number => sum + (r.rating ?? 0),
            0,
          ) /
            reviewsWithRating.length) *
            10,
        ) / 10
      : null;

  return (
    <div className="container--5 container--t container--center review">
      <h1>{t('reviews.title')}</h1>

      <ReviewAuthenticityNotice />

      {averageRating !== null && (
        <div className="reviews--aggregate">
          <span className="reviews--aggregate--value">{averageRating}</span>
          <AggregateStars
            rating={averageRating}
            count={reviewsWithRating.length}
          />
        </div>
      )}

      {Reviews.reviews.map((review: IEshopReview): React.JSX.Element => {
        const key: string = `${review.date}-${review.name}-${review.rating}`;

        const header: string = `${review.flag} ${review.name?.trim() ?? ''}`;

        const hasText = (d: IEshopReviewDescription): boolean =>
          Boolean(d.description?.trim());

        const languageDescription: IEshopReviewDescription | undefined =
          review.descriptions?.find(
            (d: IEshopReviewDescription): boolean =>
              d.languageType.code === Language.languageType.code,
          );

        const description: IEshopReviewDescription | undefined =
          (languageDescription && hasText(languageDescription)
            ? languageDescription
            : review.descriptions?.find(hasText)) ??
          languageDescription ??
          review.descriptions?.[0];

        const prosEls: React.JSX.Element[] = [];
        const consEls: React.JSX.Element[] = [];

        if (description) {
          description.pros?.forEach((pro: string): void => {
            prosEls.push(
              <div
                key={pro}
                className="review review--pros-cons-list review--pros-icon"
              >
                <Add />
                <span>{pro}</span>
              </div>,
            );
          });

          description.cons?.forEach((con: string): void => {
            consEls.push(
              <div
                key={con}
                className="review review--pros-cons-list review--cons-icon"
              >
                <Remove />
                <span>{con}</span>
              </div>,
            );
          });
        }

        return (
          <article key={key} aria-label={header} className="review-card">
            <div className="review-card--header">
              <p className="review-card--name">{header}</p>
              <p className="review-card--date">
                {review.date.split('-').reverse().join('.')}
              </p>
            </div>

            {review.rating && (
              <Stars initialRating={review.rating} isDisabled />
            )}

            {description && hasText(description) && (
              <p className="review-card--description">
                {description.description}
              </p>
            )}

            {description && (description.pros || description.cons) && (
              <div className="container">
                <div className="container--5">
                  <h4>{t('review.pros')}</h4>
                  {prosEls}
                </div>
                <div className="container--5">
                  <h4>{t('review.cons')}</h4>
                  {consEls}
                </div>
              </div>
            )}
          </article>
        );
      })}
    </div>
  );
}

const mapStateToProps = ({
  Reviews,
  Language,
}: {
  Reviews: IReviewsState;
  Language: ILanguageState;
}) => ({
  Reviews,
  Language,
});

export default connect(mapStateToProps)(ReviewsContainer);
